import * as yup from 'yup';

import type { ScreenOnTime } from '@/entities/screen';

export const emailSchema = yup
  .string()
  .email('Please enter a valid email address')
  .required('Email is required');

export const passwordSchema = yup
  .string()
  .min(6, 'Password must be at least 6 characters')
  .required('Password is required');

export const confirmPasswordSchema = (passwordFieldName = 'password') =>
  yup
    .string()
    .oneOf([yup.ref(passwordFieldName)], 'Passwords do not match')
    .required('Please confirm your password');

export const nameSchema = yup
  .string()
  .min(2, 'Name must be at least 2 characters')
  .max(50, 'Name must be less than 50 characters')
  .required('Name is required');

export const loginSchema = yup.object({
  email: emailSchema,
  password: passwordSchema,
});

export const registerSchema = yup.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: confirmPasswordSchema(),
});

export const forgotPasswordSchema = yup.object({
  email: emailSchema,
});

export const resetPasswordSchema = yup.object({
  password: passwordSchema,
  confirmPassword: confirmPasswordSchema(),
});

export const profileSettingsSchema = yup.object({
  displayName: nameSchema,
  firstName: yup
    .string()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters'),
  lastName: yup
    .string()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters'),
  phone: yup
    .string()
    .matches(/^[\+]?[1-9][\d]{0,15}$/, 'Please enter a valid phone number'),
  bio: yup.string().max(500, 'Bio must be less than 500 characters'),
  avatarUrl: yup.string().url('Please enter a valid URL').nullable(),
});

export const createPartnerSchema = yup.object({
  name: yup.string().trim().required('Partner name is required'),
  networkCode: yup
    .string()
    .default('')
    .matches(/^\d*$/, 'Network code must be numeric'),
  landscapeUrl: yup.string().default(''),
  portraitUrl: yup.string().default(''),
});

export const normalizeSizeValue = (val: string): string => {
  const trimmed = val.trim();
  if (/^\d+\.\d+$/.test(trimmed)) {
    const [ft, inch] = trimmed.split('.');
    return `${ft}'${inch}''`;
  }
  if (/^\d+$/.test(trimmed)) return `${trimmed}'`;
  if (/^\d+'\d+$/.test(trimmed)) return `${trimmed}''`;
  return trimmed;
};

export const SIZE_FIELD_REGEX =
  /^[1-9]\d{0,2}(\.(1[01]|\d))?$|^[1-9]\d{0,2}'((1[01]|\d)''?)?$/;
export const SIZE_FIELD_MESSAGE =
  "Use feet (5'), decimal (5.3), or feet'inches'' (5'11''). Max 999 ft, inches 0–11.";

export const screenOnTimeSchema = yup
  .mixed<ScreenOnTime>()
  .required('Working hours are required')
  .test(
    'valid-working-hours',
    'End time must be later than start time',
    value =>
      !value ||
      `${value.from.hours}:${value.from.minutes}` <
        `${value.to.hours}:${value.to.minutes}`
  );

export const editScreenValidationSchema = yup.object().shape({
  orientation: yup.string().required('Orientation is required'),
  screenOnTime: screenOnTimeSchema,
  sizeHeight: yup.string().matches(SIZE_FIELD_REGEX, SIZE_FIELD_MESSAGE).required('Screen height is required'),
  sizeWidth: yup.string().matches(SIZE_FIELD_REGEX, SIZE_FIELD_MESSAGE).required('Screen width is required'),
  price: yup.number()
    .typeError('Price must be a number')
    .min(0, 'Price cannot be negative')
    .required('Price is required'),
  loopDuration: yup.number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(60, 'Loop duration must be at least 60 seconds')
    .required('Loop duration is required'),
  slotTimeInSec: yup.number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(1, 'Slot duration must be at least 1 second')
    .max(300, 'Maximum slot duration is 300 seconds (5 min)')
    .required('Slot duration is required'),
  volume: yup.number()
    .typeError('Volume must be a number')
    .min(0, 'Volume must be between 0 and 100')
    .max(100, 'Volume must be between 0 and 100')
    .required('Volume is required'),
  brightness: yup.number()
    .typeError('Brightness must be a number')
    .min(0, 'Brightness must be between 0 and 100')
    .max(100, 'Brightness must be between 0 and 100')
    .required('Brightness is required'),
  networkName: yup.string()
    .max(150, 'Network name cannot exceed 150 characters')
    .optional(),
});

export type LoginFormData = yup.InferType<typeof loginSchema>;
export type RegisterFormData = yup.InferType<typeof registerSchema>;
export type ForgotPasswordFormData = yup.InferType<typeof forgotPasswordSchema>;
export type ResetPasswordFormData = yup.InferType<typeof resetPasswordSchema>;
export type ProfileSettingsFormData = yup.InferType<
  typeof profileSettingsSchema
>;
export type CreatePartnerFormData = yup.InferType<typeof createPartnerSchema>;
